import React from "react"
import {
  type Application,
  type ExAppId,
  type MortgageDetails,
  type UserDetails,
  type Availability,
  type DocumentsAvailability,
} from "./types"
import { type OnlineStatus, type OpeningHours } from "./openingHoursLib"
import { type Eligibilities } from "./eligibilities"
import { type RemoteData, NotAsked, Success } from "@heyhabito/remote-data"

type Dictionary<A> = Record<ExAppId, A>

type AccountState = UserDetails

export interface AppState {
  account: AccountState | null
  applicationsRequest: RemoteData<Error, Dictionary<Application>>
  mortgageDetails: Dictionary<MortgageDetails>
  mortgageExpertsOnlineStatus: OnlineStatus
  completionAvailabilities: Dictionary<Availability | null> | null
  documentsAvailabilities: Dictionary<DocumentsAvailability | null> | null
  plusAvailabilities: Dictionary<boolean | null> | null
  openingHours: RemoteData<Error, OpeningHours>
  eligibilities: RemoteData<Error, Eligibilities>
}

export const initialAppState: AppState = {
  account: null,
  applicationsRequest: NotAsked,
  mortgageDetails: {},
  mortgageExpertsOnlineStatus: "Unknown",
  completionAvailabilities: null,
  documentsAvailabilities: null,
  plusAvailabilities: null,
  openingHours: NotAsked,
  eligibilities: NotAsked,
}

type AppAction =
  | { type: "setAccount"; account: AccountState }
  | {
      type: "setApplicationsRequest"
      applicationsRequest: RemoteData<Error, Dictionary<Application>>
    }
  | { type: "setApplication"; exAppId: ExAppId; application: Application }
  | {
      type: "setAvailabilities"
      completionAvailabilities: Dictionary<Availability | null> | null
      documentsAvailabilities: Dictionary<DocumentsAvailability | null> | null
      plusAvailabilities: Dictionary<boolean> | null
    }
  | {
      type: "setOpeningHours"
      openingHours: RemoteData<Error, OpeningHours>
    }
  | {
      type: "setMortgageExpertsOnlineStatus"
      mortgageExpertsOnlineStatus: OnlineStatus
    }
  | {
      type: "setEligibilities"
      eligibilities: RemoteData<Error, Eligibilities>
    }
  | {
      type: "setMortgageDetails"
      exAppId: ExAppId
      mortgageDetails: MortgageDetails
    }

export const appStateReducer = (
  state: AppState,
  action: AppAction
): AppState => {
  switch (action.type) {
    case "setAccount":
      return { ...state, account: action.account }

    case "setApplicationsRequest":
      return { ...state, applicationsRequest: action.applicationsRequest }

    case "setApplication":
      switch (state.applicationsRequest._tag) {
        case "Success":
          return {
            ...state,
            applicationsRequest: Success({
              ...state.applicationsRequest.result,
              [action.exAppId]: action.application,
            }),
          }
        default:
          return state
      }

    case "setMortgageExpertsOnlineStatus":
      return {
        ...state,
        mortgageExpertsOnlineStatus: action.mortgageExpertsOnlineStatus,
      }

    case "setAvailabilities":
      return {
        ...state,
        completionAvailabilities: {
          ...state.completionAvailabilities,
          ...action.completionAvailabilities,
        },
        documentsAvailabilities: {
          ...state.documentsAvailabilities,
          ...action.documentsAvailabilities,
        },
        plusAvailabilities: {
          ...state.plusAvailabilities,
          ...action.plusAvailabilities,
        },
      }

    case "setOpeningHours":
      return {
        ...state,
        openingHours: action.openingHours,
      }

    case "setEligibilities":
      return {
        ...state,
        eligibilities: action.eligibilities,
      }

    case "setMortgageDetails":
      return {
        ...state,
        mortgageDetails: {
          ...state.mortgageDetails,
          [action.exAppId]: action.mortgageDetails,
        },
      }
  }
}

interface AppStateContext {
  state: AppState
  dispatch: (action: AppAction) => void
}
export const AppStateContext = React.createContext<AppStateContext>({
  state: initialAppState,
  dispatch: () => {},
})
